import { Stack } from 'react-bootstrap'
import { HorizontalStack, Icon } from '@ztechapps/webui.util'
import { useAiChatContext } from './Context'
import styles from './styles.module.css'

export type EmptyChatSuggestion = {
  icon: string
  text: string
}

export const DEFAULT_SUGGESTIONS: EmptyChatSuggestion[] = [
  { icon: 'search', text: 'Search for customers or products' },
  { icon: 'cart-shopping', text: 'Help me create a new order' },
  { icon: 'truck-fast', text: 'Help me create a new purchase order' },
  { icon: 'magnifying-glass-chart', text: 'Find guides, blogs or help documents related to: ' },
]

/**
 * EmptyChat component displays a placeholder when there are no messages.
 * The suggestion cards are configurable per-surface so narrower-scope
 * surfaces (e.g. storefront) don't advertise admin-only capabilities that
 * the prompt will refuse.
 */
export const EmptyChat = () => {
  const { emptyChatSuggestions, assistantName, greeting } = useAiChatContext()
  const suggestions = emptyChatSuggestions ?? DEFAULT_SUGGESTIONS

  return (
    <Stack gap={3} className="align-items-center h-100 text-center p-4">
      <div className="rounded-circle bg-primary-subtle p-4 mt-auto display-6">
        <Icon name="robot" className={styles.avatarIcon} />
      </div>

      <div>
        <h5 className="mb-2 text-body">{assistantName}</h5>
        <p className="text-muted mb-0">{greeting}</p>
      </div>

      {/* Stack adds `flex: 1 1 auto` + `align-self: stretch`; neither fits here — the auto margins centre the block, and it sizes to its widest card */}
      <Stack gap={2} className="mt-3 mb-auto flex-grow-0 align-self-center">
        {suggestions.map((suggestion) => (
          <SuggestionCard key={suggestion.text} icon={suggestion.icon} text={suggestion.text} />
        ))}
      </Stack>
    </Stack>
  )
}

type SuggestionCardProps = {
  icon: string
  text: string
}

const SuggestionCard = ({ icon, text }: SuggestionCardProps) => {
  const { setInput } = useAiChatContext()

  return (
    <a
      href="#!"
      onClick={(e) => {
        e.preventDefault()
        setInput(text)
      }}
    >
      <HorizontalStack
        className={`${styles.suggestionCard} align-items-center gap-2 p-2 rounded bg-body-secondary text-start`}
      >
        <span className="flex-shrink-0 text-center" style={{ width: '1.5rem' }}>
          <Icon name={icon} className="text-secondary" />
        </span>
        <span className="small text-body">{text}</span>
      </HorizontalStack>
    </a>
  )
}
